import { Link } from "react-router-dom";
import { useLang } from "@/lib/lang";
import PublicSiteHeader from "@/components/PublicSiteHeader";
import { LANDING_VALUES } from "@/constants/landingValues";
import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowLeft } from "lucide-react";

export default function Landing() {
  const { t, lang } = useLang();
  const Arrow = lang === "ar" ? ArrowLeft : ArrowRight;

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="landing-page">
      <PublicSiteHeader />

      <main className="flex-1">
        <section className="px-8 lg:px-14 py-16 lg:py-24 border-b border-slate-200">
          <div className="max-w-3xl">
            <div className="eyebrow mb-5">{t("landing_eyebrow")}</div>
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-slate-900 tracking-tight leading-[1.05] mb-6 text-balance">
              {t("landing_title")}
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed max-w-2xl mb-10">
              {t("landing_sub")}
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                asChild
                data-testid="landing-hero-cta"
                className="rounded-none bg-slate-900 hover:bg-slate-800 text-white h-12 px-6 text-sm gap-3"
              >
                <Link to="/login">
                  {t("landing_cta")}
                  <Arrow size={14} />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                data-testid="landing-hero-sign-in"
                className="rounded-none border-slate-300 h-12 px-6 text-sm"
              >
                <Link to="/login">{t("landing_sign_in")}</Link>
              </Button>
              <Button
                asChild
                variant="ghost"
                data-testid="landing-hiw-link"
                className="rounded-none h-12 px-4 text-sm text-slate-600"
              >
                <Link to="/how-it-works">{t("hiw_nav")}</Link>
              </Button>
            </div>
          </div>
        </section>

        <section className="px-8 lg:px-14 py-16 lg:py-20 bg-slate-900 text-white">
          <div className="max-w-5xl mx-auto">
            <div className="eyebrow !text-white/60 mb-10">{t("value_eyebrow")}</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10">
              {LANDING_VALUES.map((v) => {
                const Icon = v.icon;
                return (
                  <div
                    key={v.titleKey}
                    data-testid={`landing-value-${v.titleKey}`}
                    className="bg-slate-900 p-8 lg:p-10"
                  >
                    <div className="w-11 h-11 grid-border border-white/20 flex items-center justify-center mb-5">
                      <Icon size={18} strokeWidth={1.5} />
                    </div>
                    <h2 className="font-serif-display text-2xl mb-3">{t(v.titleKey)}</h2>
                    <p className="text-sm text-white/60 leading-relaxed mb-4">{t(v.descKey)}</p>
                    {v.bulletKeys?.length > 0 && (
                      <ul className="space-y-1.5">
                        {v.bulletKeys.map((key) => (
                          <li key={key} className="text-xs text-white/45 leading-relaxed flex gap-2">
                            <span className="text-white/30 flex-shrink-0">·</span>
                            <span>{t(key)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="px-8 lg:px-14 py-16 lg:py-20 border-t border-slate-200">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-serif-display text-3xl sm:text-4xl text-slate-900 tracking-tight mb-4 text-balance">
              {t("landing_closing_title")}
            </h2>
            <p className="text-slate-600 leading-relaxed mb-8">{t("landing_closing_sub")}</p>
            <Button
              asChild
              data-testid="landing-closing-cta"
              className="rounded-none bg-slate-900 hover:bg-slate-800 text-white h-12 px-8 text-sm gap-3"
            >
              <Link to="/login">
                {t("landing_cta")}
                <Arrow size={14} />
              </Link>
            </Button>
          </div>
        </section>
      </main>

      <footer className="px-8 lg:px-14 py-6 border-t border-slate-200">
        <p className="eyebrow !text-[10px] text-slate-400 text-center">{t("login_footer")}</p>
      </footer>
    </div>
  );
}
